import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { User } from '../types';
import { firebaseService } from '../services/firebase.service';
import { apiService } from '../services/api.service';
import { debugWarn } from '../utils/debug';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  login: (email: string, password: string, sessionId?: string) => Promise<User>;
  signup: (email: string, password: string, displayName?: string, sessionId?: string) => Promise<User>;
  loginWithGoogle: (sessionId?: string) => Promise<User>;
  logout: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  updateProfile: (updates: { displayName?: string; photoURL?: string }) => Promise<void>;
  sendVerificationEmail: (redirectUrl?: string) => Promise<void>;
  refreshUser: () => Promise<User | null>;
  applyEmailVerificationCode: (actionCode: string) => Promise<User | null>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = firebaseService.onAuthStateChange((firebaseUser) => {
      if (firebaseUser) {
        setUser(firebaseUser);
      } else {
        setUser(null);
      }
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const syncAuthSession = async (sessionId?: string) => {
    try {
      const authToken = await firebaseService.getIdToken();
      await apiService.createAuthSession(sessionId ? { sessionId } : {}, authToken);
    } catch (error) {
      debugWarn('Auth session sync failed', error);
    }
  };

  const login = async (email: string, password: string, sessionId?: string): Promise<User> => {
    const user = await firebaseService.signInWithEmailPassword(email, password);
    setUser(user);
    await syncAuthSession(sessionId);
    return user;
  };

  const signup = async (
    email: string,
    password: string,
    displayName?: string,
    sessionId?: string
  ): Promise<User> => {
    const user = await firebaseService.createUserWithEmailPassword(email, password, displayName);
    setUser(user);
    await syncAuthSession(sessionId);
    return user;
  };

  const loginWithGoogle = async (sessionId?: string): Promise<User> => {
    const user = await firebaseService.signInWithGoogle();
    setUser(user);
    await syncAuthSession(sessionId);
    return user;
  };

  const logout = async (): Promise<void> => {
    await firebaseService.signOut();
    setUser(null);
  };

  const resetPassword = async (email: string): Promise<void> => {
    await firebaseService.resetPassword(email);
  };

  const updateProfile = async (updates: { displayName?: string; photoURL?: string }): Promise<void> => {
    await firebaseService.updateUserProfile(updates);
    if (user) {
      setUser({ ...user, ...updates });
    }
  };

  const sendVerificationEmail = async (redirectUrl?: string): Promise<void> => {
    await firebaseService.sendVerificationEmail(redirectUrl);
  };

  const refreshUser = async (): Promise<User | null> => {
    const refreshedUser = await firebaseService.reloadCurrentUser();
    if (refreshedUser) {
      setUser(refreshedUser);
      return refreshedUser;
    }
    return null;
  };

  const applyEmailVerificationCode = async (actionCode: string): Promise<User | null> => {
    const refreshedUser = await firebaseService.applyEmailVerificationCode(actionCode);
    if (refreshedUser) {
      setUser(refreshedUser);
      return refreshedUser;
    }
    return null;
  };

  const value: AuthContextType = {
    user,
    loading,
    login,
    signup,
    loginWithGoogle,
    logout,
    resetPassword,
    updateProfile,
    sendVerificationEmail,
    refreshUser,
    applyEmailVerificationCode,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
